import { Component, Output, EventEmitter, inject, HostListener, effect, OnInit, OnDestroy, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthStateService } from '@dental-ui/auth';
import { AppNavigationService } from '@dental-ui/shared/app-navigation';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';
import { DropdownComponent, DropdownOption } from '../dropdown/dropdown.component';
import { UserBranchStateService } from '@dental-ui/user-branch';
import { Subscription } from 'rxjs';
import { calculateInitials, getInitial } from '@dental-ui/util';
import { ProfileAvatarDialogComponent } from '../profile-avatar-dialog/profile-avatar-dialog.component';

@Component({
  selector: 'lib-header',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslatePipe, DropdownComponent, ProfileAvatarDialogComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() toggleMenu = new EventEmitter<void>();

  private readonly authStateService = inject(AuthStateService);
  private readonly router = inject(Router);
  private readonly appNavigation = inject(AppNavigationService);
  private readonly translateService = inject(TranslateService);
  private readonly userBranchStateService = inject(UserBranchStateService);
  private languageChangeSubscription?: Subscription;

  userFullName = '';
  userInitials = '';
  branchDescription = '';
  isDropdownOpen = false;
  currentLanguage = 'en';
  selectedBranchId = signal<string | number | null>(null);
  showAvatarDialog = signal<boolean>(false);
  userAvatarUrl = signal<string | null>(null);

  readonly languageOptions: DropdownOption[] = [
    { value: 'en', label: 'EN' },
    { value: 'tr', label: 'TR' },
  ];

  readonly userBranches = this.userBranchStateService.userBranches;
  readonly isLoadingBranches = this.userBranchStateService.isLoading;

  readonly branchOptions = computed<DropdownOption[]>(() => {
    const branches = this.userBranches();
    return branches.map(branch => ({
      value: branch.id,
      label: branch.branchDescription || '',
    }));
  });
  readonly isAuthorizedForAccount = computed(() => {
    const role = this.authStateService.user()?.role;
    return role === 'AccountManager' || role === 'LaboratoryManager';
  });

  constructor() {
    effect(() => {
      const user = this.authStateService.user();
      const lang = this.translateService.getCurrentLanguage() || 'tr';
      if (user?.firstName && user?.lastName) {
        this.userFullName = `${user.firstName} ${user.lastName}`;
        this.userInitials = calculateInitials(user.firstName, user.lastName, lang);
        this.branchDescription = user.branchDescription || '';
      } else if (user?.email) {
        this.userFullName = user.email;
        this.userInitials = getInitial(user.email).toLocaleUpperCase(lang);
        this.branchDescription = user.branchDescription || '';
      } else {
        this.userFullName = '';
        this.userInitials = '';
        this.branchDescription = '';
      }

      if (user?.userAvatarUrl) {
        const url = user.userAvatarUrl;
        const separator = url.includes('?') ? '&' : '?';
        this.userAvatarUrl.set(`${url}${separator}t=${new Date().getTime()}`);
      } else {
        this.userAvatarUrl.set(null);
      }

      // If initials are still empty but we have a name, fallback to first available char
      if (!this.userInitials && this.userFullName) {
        const match = this.userFullName.match(/[a-zA-Z0-9ÇçĞğİiIıÖöŞşÜü]/);
        this.userInitials = match ? match[0].toUpperCase() : '';
      }
    });

    // Mevcut dili al
    this.currentLanguage = this.translateService.getCurrentLanguage();

    // User branches değiştiğinde seçili branch'i güncelle
    effect(() => {
      const branches = this.userBranches();
      const currentBranchId = this.selectedBranchId();
      
      // Eğer seçili branch yoksa ve branchDescription varsa, eşleşen branch'i bul
      if (!currentBranchId && this.branchDescription && branches.length > 0) {
        const matchingBranch = branches.find(b => b.branchDescription === this.branchDescription);
        if (matchingBranch) {
          this.selectedBranchId.set(matchingBranch.id);
        } else if (branches.length > 0) {
          // Eşleşen yoksa ilk branch'i seç
          this.selectedBranchId.set(branches[0].id);
        }
      }
    });
  }

  ngOnInit(): void {
    // İlk yükleme - translation dosyalarını yükle
    this.loadTranslations();

    // Listen to language changes from AuthStateService
    this.languageChangeSubscription = this.authStateService.languageChange$.subscribe((lang) => {
      this.currentLanguage = lang;
      // Dil değiştiğinde translation dosyalarını yeniden yükle
      this.loadTranslations();
    });

    // User branches verilerini yükle
    this.userBranchStateService.loadAllUserBranches().subscribe();
  }

  ngOnDestroy(): void {
    // Subscription'ı temizle
    this.languageChangeSubscription?.unsubscribe();
  }

  /**
   * Translation dosyalarını yükler
   * Bu metod hem ilk yüklemede hem de dil değiştiğinde çağrılır
   */
  private loadTranslations(): void {
    // Shared UI translation dosyalarını yükle (eğer varsa)
    // Şu an için merkezi translation dosyaları kullanılıyor
    // İleride shared/ui için özel translation dosyaları eklenebilir
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu')) {
      this.closeDropdown();
    }
  }

  onToggleMenu(): void {
    this.toggleMenu.emit();
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  onAvatarClick(event: Event): void {
    event.stopPropagation();
    this.showAvatarDialog.set(true);
  }

  onAvatarDialogClose(): void {
    this.showAvatarDialog.set(false);
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  navigateToProfile(): void {
    if (!this.isAuthorizedForAccount()) {
      return;
    }
    this.closeDropdown();
    this.authStateService.navigateToApp('account', '/account');
  }

  logout(): void {
    this.closeDropdown();
    this.authStateService.logout();
  }

  onLanguageChange(languageCode: string | number): void {
    if (!languageCode) {
      return;
    }

    const langCode = String(languageCode);
    this.translateService.setLanguage(langCode).catch(err => {
      console.error('Failed to change language:', err);
    });

    this.authStateService.setLanguageCookie(langCode);

    // URL'den language parametresini temizle
    this.removeLanguageParamFromUrl();
  }

  /**
   * URL'den language parametresini temizler (hem query string'den hem de hash'ten)
   */
  private removeLanguageParamFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    let hasChanges = false;

    // Query string'den language parametresini temizle
    if (url.searchParams.has('language')) {
      url.searchParams.delete('language');
      hasChanges = true;
    }

    // Hash içindeki query parametrelerini kontrol et ve temizle
    if (url.hash && url.hash.includes('?')) {
      const hashParts = url.hash.split('?');
      if (hashParts.length === 2) {
        const hashParams = new URLSearchParams(hashParts[1]);
        if (hashParams.has('language')) {
          hashParams.delete('language');
          const newHashParams = hashParams.toString();
          url.hash = hashParts[0] + (newHashParams ? `?${newHashParams}` : '');
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      window.history.replaceState({}, '', url.toString());
    }
  }

  getCurrentLanguageLabel(): string {
    const option = this.languageOptions.find(opt => opt.value === this.currentLanguage);
    return option ? `${option.label}` : this.translateService.getText('COMMON.LANGUAGE') || 'Language';
  }

  onBranchChange(branchId: string | number): void {
    this.selectedBranchId.set(branchId);
    const branches = this.userBranches();
    const selectedBranch = branches.find(b => b.id === branchId);
    if (selectedBranch) {
      this.branchDescription = selectedBranch.branchDescription;
      // Branch değişikliğini işle (gerekirse API çağrısı yapılabilir)
    }
  }
}

